import { AttachmentMIMEType, EntityType, eventReducer, EventType, } from "@withorbit/core";
import { testTask } from "@withorbit/sample-data";
import { AjvEventsValidator } from "./validation/AjvEventsValidator.js";
// n.b. these tests are not actually run as part of this package: they're run in the implementation packages.
function mockEventReducer(entitySnapshot, event) {
    return {
        ...testTask,
        id: event.entityID,
        eventIDs: entitySnapshot
            ? [...entitySnapshot.eventIDs, event.id]
            : [event.id],
    };
}
const mockValidator = {
    validateEvents: () => true,
};
const testEvents = [
    { id: "a", entityID: "x", timestampMillis: 100 },
    { id: "b", entityID: "x", timestampMillis: 90 },
    { id: "c", entityID: "y", timestampMillis: 95 },
];
export function runDatabaseTests(name, databaseFactory, runMetadataTests = true) {
    let db;
    beforeEach(async () => {
        db = await databaseFactory(mockEventReducer, mockValidator);
    });
    afterEach(async () => {
        await db.close();
    });
    describe(`${name} database tests`, () => {
        test(`round-trip events`, async () => {
            await db.putEvents(testEvents);
            const result = await db.getEvents(["a", "c"]);
            expect(result).toEqual(new Map([
                ["a", testEvents[0]],
                ["c", testEvents[2]],
            ]));
        });
        test("empty event list doesn't explode", async () => {
            await db.putEvents([]);
        });
        test("put duplicate events", async () => {
            await db.putEvents(testEvents);
            await db.putEvents([
                { id: "e", entityID: "y", timestampMillis: 105 },
                ...testEvents,
                { id: "d", entityID: "y", timestampMillis: 90 },
            ]);
            const allEvents = await db.listEvents({});
            expect(allEvents.length).toEqual(testEvents.length + 2);
        });
        test(`updates entities`, async () => {
            await db.putEvents(testEvents);
            const entityID = "x";
            let results = await db.getEntities([entityID]);
            let entity = results.get(entityID);
            expect(entity.eventIDs).toEqual(["b", "a"]);
            // Out-of-order event:
            await db.putEvents([
                { id: "z", entityID: "x", timestampMillis: 94 },
            ]);
            results = await db.getEntities([entityID]);
            entity = results.get(entityID);
            expect(entity.eventIDs).toEqual(["b", "z", "a"]);
            // Fast-forward event:
            await db.putEvents([
                { id: "q", entityID: "x", timestampMillis: 110 },
            ]);
            results = await db.getEntities([entityID]);
            entity = results.get(entityID);
            expect(entity.eventIDs).toEqual(["b", "z", "a", "q"]);
        });
        test("doesn't write events which fail to apply", async () => {
            const db = await databaseFactory(eventReducer, mockValidator);
            await expect(db.putEvents([
                {
                    type: EventType.TaskUpdateDeleted,
                    entityID: "zz",
                    id: "fail",
                    timestampMillis: 100,
                    isDeleted: true,
                },
            ])).rejects.toBeInstanceOf(Error);
            const events = await db.getEvents(["fail"]);
            expect(events.size).toBe(0);
        });
        test("events with same timestamp are combined with original order", async () => {
            const entityID = "x";
            await db.putEvents([
                { id: "b", entityID, timestampMillis: 100 },
                { id: "a", entityID, timestampMillis: 100 },
            ]);
            const resultMap = await db.getEntities([entityID]);
            const entity = resultMap.get(entityID);
            expect(entity.eventIDs).toEqual(["b", "a"]);
        });
        test(`maintains entity ordering`, async () => {
            await db.putEvents(testEvents);
            const initialEntities = await db.listEntities({
                entityType: EntityType.Task,
            });
            await db.putEvents([
                { id: "z", entityID: "x", timestampMillis: 94 },
            ]);
            const modifiedEntities = await db.listEntities({
                entityType: EntityType.Task,
            });
            expect(modifiedEntities.map(({ id }) => id)).toEqual(initialEntities.map(({ id }) => id));
        });
        if (runMetadataTests) {
            describe("metadata", () => {
                test("round-trips", async () => {
                    const testValues = new Map([
                        ["foo", "bar"],
                        ["baz", "bat"],
                    ]);
                    await db.setMetadataValues(testValues);
                    const result = await db.getMetadataValues(["foo", "baz", "quux"]);
                    expect(result.size).toEqual(2);
                    expect(result).toMatchObject(testValues);
                });
                test("overwrites old values", async () => {
                    await db.setMetadataValues(new Map([["foo", "bar"]]));
                    await db.setMetadataValues(new Map([["foo", "baz"]]));
                    const result = await db.getMetadataValues(["foo"]);
                    expect(result.size).toEqual(1);
                    expect(result.get("foo")).toEqual("baz");
                });
                test("missing values are null", async () => {
                    const result = await db.getMetadataValues(["test"]);
                    expect(result.size).toEqual(0);
                });
                test("deleting value", async () => {
                    await db.setMetadataValues(new Map([["foo", "bar"]]));
                    await db.setMetadataValues(new Map([["foo", null]]));
                    const result = await db.getMetadataValues(["foo"]);
                    expect(result.size).toEqual(0);
                });
            });
        }
        describe("querying events", () => {
            beforeEach(() => db.putEvents(testEvents));
            test(`by entity ID`, async () => {
                const events = await db.listEvents({
                    predicate: ["entityID", "=", "x"],
                });
                expect(events).toEqual([testEvents[0], testEvents[1]]);
            });
            test(`by entity ID and limit`, async () => {
                const events = await db.listEvents({
                    predicate: ["entityID", "=", "x"],
                    limit: 1,
                });
                expect(events).toEqual([testEvents[0]]);
            });
            test(`with after ID`, async () => {
                const events = await db.listEvents({
                    afterID: "a",
                });
                expect(events).toEqual([testEvents[1], testEvents[2]]);
            });
            test(`by entity ID and after ID`, async () => {
                const events = await db.listEvents({
                    afterID: "a",
                    predicate: ["entityID", "=", "x"],
                });
                expect(events).toEqual([testEvents[1]]);
            });
        });
        describe("querying entities", () => {
            // These tests use the real Orbit event reducer.
            let db;
            beforeEach(async () => {
                db = await databaseFactory(eventReducer, mockValidator);
                await db.putEvents(testTaskEvents);
                await db.putEvents(testAttachmentEvents);
            });
            afterEach(async () => {
                await db.close();
            });
            test("get tasks by ID", async () => {
                const result = await db.getEntities(["a", "c", "z"]);
                expect(result.size).toBe(2);
                expect(result.has("a")).toBe(true);
                expect(result.has("c")).toBe(true);
                expect(result.has("z")).toBe(false);
                const aTask = result.get("a");
                expect(aTask.type).toBe(EntityType.Task);
                expect(aTask.spec).toEqual(testTaskEvents[0].spec);
                expect(aTask.isDeleted).toBe(false);
                expect(aTask.componentStates["a"].createdAtTimestampMillis).toEqual(testTaskEvents[0].timestampMillis);
            });
            test("get attachments by ID", async () => {
                const result = await db.getEntities(["a_a", "z_a"]);
                expect(result.size).toBe(1);
                expect(result.has("a_a")).toBe(true);
                expect(result.has("z_a")).toBe(false);
            });
            test("limit", async () => {
                const entities = await db.listEntities({
                    entityType: EntityType.Task,
                    limit: 1,
                });
                expect(entities.length).toBe(1);
                expect(entities[0].id).toBe("q");
            });
            test("after", async () => {
                const entities = await db.listEntities({
                    entityType: EntityType.Task,
                    afterID: "a",
                });
                expect(entities.length).toBe(2);
                expect(entities[0].id).toBe("b");
                expect(entities[1].id).toBe("c");
            });
            test("tasks by due timestamp", async () => {
                const entities = await db.listEntities({
                    entityType: EntityType.Task,
                    predicate: ["dueTimestampMillis", "<=", 100],
                });
                expect(entities.length).toBe(2);
                expect(entities[0].id).toBe("a");
                expect(entities[1].id).toBe("b");
            });
            test("tasks by due timestamp, limit", async () => {
                const entities = await db.listEntities({
                    entityType: EntityType.Task,
                    limit: 1,
                    predicate: ["dueTimestampMillis", "<=", 100],
                });
                expect(entities.length).toBe(1);
                expect(entities[0].id).toBe("a");
            });
            test("tasks by due timestamp, afterID", async () => {
                const entities = await db.listEntities({
                    entityType: EntityType.Task,
                    afterID: "a",
                    predicate: ["dueTimestampMillis", "<=", 150],
                });
                expect(entities.length).toBe(2);
                expect(entities[0].id).toBe("b");
                expect(entities[1].id).toBe("c");
            });
            test("tasks by due timestamp, afterID, limit", async () => {
                const entities = await db.listEntities({
                    entityType: EntityType.Task,
                    afterID: "a",
                    limit: 1,
                    predicate: ["dueTimestampMillis", "<=", 150],
                });
                expect(entities.length).toBe(1);
                expect(entities[0].id).toBe("b");
            });
            test("attachment references", async () => {
                const entities = await db.listEntities({
                    entityType: EntityType.AttachmentReference,
                });
                expect(entities.length).toBe(2);
                expect(entities[0].id).toBe("a_a");
                expect(entities[1].id).toBe("b_a");
            });
        });
        describe("validation", () => {
            // These tests use the real Orbit event validator
            let db;
            beforeEach(async () => {
                db = await databaseFactory(eventReducer, new AjvEventsValidator());
            });
            afterEach(async () => {
                await db.close();
            });
            test("rejects non-array events", async () => {
                // @ts-expect-error
                const input = { someNonArrayType: true };
                await expect(db.putEvents(input)).rejects.toEqual({
                    errors: [{ message: "#/type must be array" }],
                });
            });
            test("rejects invalid events", async () => {
                const invalidTestEvents = [...createTestTaskEvents("a", [50, 300], 5)];
                await expect(db.putEvents(invalidTestEvents)).rejects.toBeDefined();
            });
            test("accepts valid input", async () => {
                await expect(db.putEvents(transformTestEventsToHaveValidIDs(testTaskEvents))).resolves.toBeDefined();
            });
        });
    });
}
function createTestTaskEvents(taskID, componentDueTimestampsMillis, eventTimestampMillis) {
    return [
        {
            id: `${taskID}_event_ingest`,
            type: EventType.TaskIngest,
            timestampMillis: eventTimestampMillis,
            entityID: taskID,
            spec: testTask.spec,
            provenance: null,
        },
        {
            id: `${taskID}_event_reschedule_a`,
            type: EventType.TaskReschedule,
            componentID: "a",
            timestampMillis: eventTimestampMillis,
            newDueTimestampMillis: componentDueTimestampsMillis[0],
            entityID: taskID,
        },
        {
            id: `${taskID}_event_reschedule_b`,
            type: EventType.TaskReschedule,
            componentID: "b",
            timestampMillis: eventTimestampMillis,
            newDueTimestampMillis: componentDueTimestampsMillis[1],
            entityID: taskID,
        },
    ];
}
function transformTestEventsToHaveValidIDs(events) {
    // IDs must be at least 22 characters long
    const EVENT_ID_LENGTH = 22;
    const createValidID = (str) => {
        const missingCharsAsZeros = Array(EVENT_ID_LENGTH - str.length + 1).join("0");
        return `${str}${missingCharsAsZeros}`;
    };
    return events.map((event) => ({
        ...event,
        id: createValidID(event.id),
        entityID: createValidID(event.entityID),
    }));
}
const testTaskEvents = [
    ...createTestTaskEvents("q", [50, 300], 5),
    ...createTestTaskEvents("a", [50, 300], 5),
    ...createTestTaskEvents("b", [100, 100], 4),
    ...createTestTaskEvents("c", [150, 150], 3),
    // ensure that deleted tasks don't show up in due timestamp listing
    {
        type: EventType.TaskUpdateDeleted,
        entityID: "q",
        id: "zzz",
        isDeleted: true,
        timestampMillis: 150,
    },
];
const testAttachmentEvents = [
    {
        id: "a_a_ingest",
        entityID: "a_a",
        type: EventType.AttachmentIngest,
        timestampMillis: 1000,
        mimeType: AttachmentMIMEType.PNG,
    },
    {
        id: "b_a_ingest",
        entityID: "b_a",
        type: EventType.AttachmentIngest,
        timestampMillis: 5000,
        mimeType: AttachmentMIMEType.PNG,
    },
];
//# sourceMappingURL=databaseTests.js.map